if (!customElements.get('custom-length')) {
  customElements.define(
    'custom-length',
    class CustomLength extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.toggle = this.querySelector('.custom-length__checkbox');
        this.panel = this.querySelector('.custom-length__panel');
        this.input = this.querySelector('.custom-length__input');
        this.note = this.querySelector('[data-custom-length-note]');
        this.errorMessage = this.querySelector('[data-custom-length-error]');

        if (!this.toggle || !this.input) return;

        this.initialized = true;
        this.optionName = this.dataset.optionName;
        this.step = parseFloat(this.dataset.step) || 0.5;
        this.requested = null;
        this.expectedValue = null;
        this.blocked = false;
        this.selecting = false;
        this.rounded = false;

        this.toggle.addEventListener('change', this.onToggle.bind(this));
        this.input.addEventListener('input', this.onInput.bind(this));
        this.input.addEventListener('blur', this.onBlur.bind(this));
        document.addEventListener('change', this.onDocumentChange.bind(this));
        document.addEventListener('click', this.onDocumentClick.bind(this), true);
      }

      // The product section is rendered twice: inline and inside the sticky floated
      // form, each with its own picker and add to cart button. This element lives
      // outside both, so it drives every copy on the page.
      get forms() {
        return Array.from(document.querySelectorAll('form[data-type="add-to-cart-form"]'));
      }

      get pickers() {
        return Array.from(document.querySelectorAll('variant-radios'));
      }

      // Standard sizes read straight from the variant picker, so they always match
      // what the theme currently offers. Values look like `36"` or `80 inch`.
      sizesIn(picker) {
        const name = this.optionName;

        return Array.from(picker.querySelectorAll('input[type="radio"]'))
          .filter((radio) => radio.name === name || radio.name.indexOf(`${name}-`) === 0)
          .map((radio) => ({ radio: radio, value: parseFloat(String(radio.value).replace(/[^0-9.]/g, '')) }))
          .filter((size) => !isNaN(size.value))
          .sort((a, b) => a.value - b.value);
      }

      get sizes() {
        for (let i = 0; i < this.pickers.length; i++) {
          const sizes = this.sizesIn(this.pickers[i]);
          if (sizes.length) return sizes;
        }

        return [];
      }

      // Smallest standard size that covers the requested length. Anything shorter
      // than the smallest size is charged as the smallest size.
      resolve(sizes, requested) {
        const candidates = sizes.filter((size) => size.value + 0.0001 >= requested);
        return candidates.find((size) => !size.radio.classList.contains('disabled')) || candidates[0];
      }

      // Keeps the property in the same format the product uses: 37.5" or 37.5 inch.
      get unit() {
        const first = this.sizes[0];
        if (!first) return '"';
        return String(first.radio.value).indexOf('"') === -1 ? ' inch' : '"';
      }

      format(value) {
        return `${Math.round(value * 100) / 100}${this.unit}`;
      }

      onToggle() {
        this.panel.hidden = !this.toggle.checked;

        if (this.toggle.checked) {
          this.input.focus();
        } else {
          this.input.value = '';
        }

        this.update();
      }

      // Each selection change makes the theme refetch the section, so wait for a
      // pause in typing. Clicking add to cart flushes this synchronously.
      onInput() {
        clearTimeout(this.inputTimer);
        this.inputTimer = setTimeout(() => this.update(), 250);
      }

      onBlur() {
        const value = parseFloat(this.input.value);
        if (isNaN(value) || value <= 0) return;

        // Snap up to the next half inch: a rod slightly too long can be trimmed,
        // one that is too short cannot.
        const snapped = Math.ceil(value / this.step - 0.0001) * this.step;
        if (snapped === value) return;

        this.input.value = String(Math.round(snapped * 100) / 100);
        this.rounded = true;
        this.update();
        this.rounded = false;
      }

      update() {
        clearTimeout(this.inputTimer);

        const value = parseFloat(this.input.value);
        const sizes = this.sizes;

        this.errorMessage.hidden = true;

        if (!this.toggle.checked || isNaN(value) || value <= 0 || !sizes.length) {
          this.clear();
          return;
        }

        const largest = sizes[sizes.length - 1];

        if (value > largest.value + 0.0001) {
          this.clear();
          this.showOversizeError(largest.value);
          this.setBlocked(true);
          return;
        }

        const match = this.resolve(sizes, value);

        this.requested = value;
        this.setBlocked(false);
        this.apply();

        let note = this.dataset.noteTemplate
          .replace('[cut]', this.format(value))
          .replace('[charged]', this.format(match.value));

        if (this.rounded) note = `${note} ${this.dataset.noteRounded}`;

        this.note.textContent = note;
        this.note.hidden = false;
      }

      // Push the current length into every add to cart form and every picker,
      // including copies that were rendered after the last change.
      apply() {
        if (this.requested === null) return;

        this.pickers.forEach((picker) => this.selectIn(picker));
        this.forms.forEach((form) => this.writeProperties(form));
      }

      // Selecting the radio and firing a bubbling change is what VariantRadios
      // listens for, so the theme updates the price, the variant id and the URL.
      selectIn(picker) {
        const match = this.resolve(this.sizesIn(picker), this.requested);
        if (!match) return;

        this.expectedValue = match.radio.value;

        if (!match.radio.checked) {
          this.selecting = true;
          match.radio.checked = true;
          match.radio.dispatchEvent(new Event('change', { bubbles: true }));
          this.selecting = false;
        }

        this.updateDropdownLabel(match.radio);
      }

      // The dropdown picker keeps its own label, which only the theme's click
      // handler updates. Programmatic selection has to update it too.
      updateDropdownLabel(radio) {
        const dropdown = radio.closest('.dropdown-select');
        const label = dropdown && dropdown.querySelector('.select-label span');
        if (!label) return;

        label.textContent = radio.value;
        label.setAttribute('title', radio.value);
      }

      // The visible property is what production reads off the order, so it names the
      // dimension the product is cut to: Custom length for the rods, Custom width for
      // the shelves. The hidden raw number keeps one key across both.
      get propertyName() {
        return this.dataset.propertyName || 'Custom length';
      }

      writeProperties(form) {
        let property = form.querySelector('.custom-length__property');

        if (!property) {
          property = this.buildProperty(`properties[${this.propertyName}]`, 'custom-length__property');
          form.appendChild(property);
        }

        let raw = form.querySelector('.custom-length__property-raw');

        if (!raw) {
          raw = this.buildProperty('properties[_custom_length_in]', 'custom-length__property-raw');
          form.appendChild(raw);
        }

        property.name = `properties[${this.propertyName}]`;
        property.value = this.format(this.requested);
        raw.value = String(Math.round(this.requested * 100) / 100);
      }

      buildProperty(name, className) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.className = className;
        return input;
      }

      // Without a custom length the forms submit exactly as they do today.
      clear() {
        this.requested = null;
        this.expectedValue = null;
        this.note.hidden = true;
        this.setBlocked(false);

        this.forms.forEach((form) => {
          form
            .querySelectorAll('.custom-length__property, .custom-length__property-raw')
            .forEach((input) => input.remove());
        });
      }

      reset() {
        this.toggle.checked = false;
        this.input.value = '';
        this.panel.hidden = true;
        this.errorMessage.hidden = true;
        this.clear();
      }

      onDocumentChange(event) {
        if (this.selecting || !this.toggle.checked || !this.expectedValue) return;

        const radio = event.target;
        if (!radio.matches || !radio.matches('input[type="radio"]')) return;

        const name = radio.name;
        if (name !== this.optionName && name.indexOf(`${this.optionName}-`) !== 0) return;
        if (radio.value === this.expectedValue) return;

        // The customer picked a different standard size by hand, so the custom
        // length no longer matches what they are paying for.
        this.reset();
      }

      onDocumentClick(event) {
        if (!this.isConnected) return;

        const button = event.target.closest && event.target.closest('[name="add"]');
        if (!button || !button.form) return;

        // Flush a pending keystroke and reach forms that loaded late, such as the
        // sticky bar. Runs before the theme's submit handler reads the form data.
        const wasEnabled = !button.hasAttribute('disabled');
        this.update();

        if (this.blocked) {
          event.preventDefault();
          event.stopPropagation();
          this.input.focus();
          return;
        }

        // Changing the selection here makes the theme disable the button until its
        // async render finishes, which would swallow this very click.
        if (wasEnabled && button.hasAttribute('disabled')) button.removeAttribute('disabled');
      }

      showOversizeError(max) {
        let message = this.dataset.errorOversize.replace('[max]', this.format(max));

        if (this.dataset.oversizeUrl) {
          message = `${message} <a href="${this.dataset.oversizeUrl}">${this.dataset.oversizeLabel}</a>`;
        }

        this.errorMessage.innerHTML = message;
        this.errorMessage.hidden = false;
      }

      setBlocked(blocked) {
        this.blocked = blocked;
        this.classList.toggle('custom-length--invalid', blocked);

        this.forms.forEach((form) => {
          const button = form.querySelector('[name="add"]');
          if (button) button.setAttribute('aria-disabled', blocked ? 'true' : 'false');
        });
      }
    }
  );
}
